/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';

export interface FilterState {
  model: string | null;
  variant: string | null;
  color: string | null;
}

interface ProductFilterProps {
  onFilterChange: (filters: FilterState) => void;
  filters: FilterState;
}

const ProductFilter: React.FC<ProductFilterProps> = ({ onFilterChange, filters }) => {
  const models = ['15', '16', '17'];
  const variants = ['base', 'Pro', 'Pro Max'];
  const colors = ['Đen', 'Trắng', 'Xanh', 'Hồng', 'Vàng'];

  const handleFilterChange = (key: keyof FilterState, value: string | null) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFilterChange({ model: null, variant: null, color: null });
  };

  const hasActiveFilters = filters.model || filters.variant || filters.color;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Lọc sản phẩm</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-slate-600 hover:text-slate-900 underline"
          >
            Xóa bộ lọc
          </button>
        )}
      </div>

      {/* iPhone Model */}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1.5">Model iPhone</label>
        <div className="flex gap-2">
          <button
            onClick={() => handleFilterChange('model', null)}
            className={`px-3 py-2 md:py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] active:scale-95 ${
              !filters.model
                ? 'bg-slate-700 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Tất cả
          </button>
          {models.map((model) => (
            <button
              key={model}
              onClick={() => handleFilterChange('model', model)}
              className={`px-3 py-2 md:py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] active:scale-95 ${
                filters.model === model
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {model}
            </button>
          ))}
        </div>
      </div>

      {/* Variant */}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1.5">Phiên bản</label>
        <div className="flex gap-2">
          <button
            onClick={() => handleFilterChange('variant', null)}
            className={`px-3 py-2 md:py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] active:scale-95 ${
              !filters.variant
                ? 'bg-slate-700 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Tất cả
          </button>
          {variants.map((variant) => (
            <button
              key={variant}
              onClick={() => handleFilterChange('variant', variant)}
              className={`px-3 py-2 md:py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] active:scale-95 ${
                filters.variant === variant
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {variant}
            </button>
          ))}
        </div>
      </div>

      {/* Color */}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1.5">Màu sắc</label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleFilterChange('color', null)}
            className={`px-3 py-2 md:py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] active:scale-95 ${
              !filters.color
                ? 'bg-slate-700 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Tất cả
          </button>
          {colors.map((color) => (
            <button
              key={color}
              onClick={() => handleFilterChange('color', color)}
              className={`px-3 py-2 md:py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] active:scale-95 ${
                filters.color === color
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {color}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductFilter;
